import { useState } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { MONEDAS } from '@shared/constants';
import { NumberField } from '@renderer/components/ui/NumberField';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  onSiguiente: () => void;
  onAtras: () => void;
}

export function Step3Impuestos({ onSiguiente, onAtras }: Props) {
  const [cobraImpuestos, setCobraImpuestos] = useState(true);
  const [impuestoNombre, setImpuestoNombre] = useState('IVA');
  const [impuestoPorcentaje, setImpuestoPorcentaje] = useState(16);
  const [monedaCodigo, setMonedaCodigo] = useState('MXN');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSiguiente = async () => {
    setEnviando(true);
    setError(null);
    const res = await window.api.onboarding.guardarPaso(3, {
      cobraImpuestos,
      impuestoNombre,
      impuestoPorcentaje: cobraImpuestos ? impuestoPorcentaje : 0,
      monedaCodigo,
    });
    setEnviando(false);
    if (res.ok) onSiguiente();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  const handleSaltar = async () => {
    setEnviando(true);
    const res = await window.api.onboarding.guardarPaso(3, {
      cobraImpuestos: true,
      impuestoNombre: 'IVA',
      impuestoPorcentaje: 16,
      monedaCodigo: 'MXN',
    });
    setEnviando(false);
    if (res.ok) onSiguiente();
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Configura impuestos y moneda</h1>
        <p className="mt-2 text-slate-500">Puedes cambiar esto en cualquier momento.</p>
      </div>

      <div className="card space-y-5">
        <div>
          <div className="label">¿Tu negocio cobra impuestos?</div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={cobraImpuestos}
                onChange={() => setCobraImpuestos(true)}
                className="h-4 w-4"
              />
              <span>Sí (recomendado)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!cobraImpuestos}
                onChange={() => setCobraImpuestos(false)}
                className="h-4 w-4"
              />
              <span>No</span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="label" htmlFor="impNombre">Nombre del impuesto</label>
            <input
              id="impNombre"
              className="input"
              value={impuestoNombre}
              onChange={(e) => setImpuestoNombre(e.target.value)}
              disabled={!cobraImpuestos}
              maxLength={20}
            />
          </div>
          <div>
            <label className="label" htmlFor="impPct">Porcentaje</label>
            <div className="relative">
              <NumberField
                id="impPct"
                className="input pr-10"
                value={impuestoPorcentaje}
                onChange={setImpuestoPorcentaje}
                disabled={!cobraImpuestos}
                min={0}
                max={100}
                decimals={1}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">%</span>
            </div>
          </div>
        </div>

        <div>
          <label className="label" htmlFor="moneda">Moneda</label>
          <select
            id="moneda"
            className="input"
            value={monedaCodigo}
            onChange={(e) => setMonedaCodigo(e.target.value)}
          >
            {MONEDAS.map((m) => (
              <option key={m.codigo} value={m.codigo}>
                {m.label} ({m.codigo}, {m.simbolo})
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</div>
      )}

      <div className="flex justify-between">
        <button onClick={onAtras} className="btn-ghost btn-lg">
          <ArrowLeft className="h-5 w-5" />
          Atrás
        </button>
        <div className="flex gap-3">
          <button onClick={handleSaltar} className="btn-secondary btn-lg" disabled={enviando}>
            Saltar
          </button>
          <button onClick={handleSiguiente} className="btn-primary btn-lg" disabled={enviando}>
            Siguiente
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
